import {
  type ActiveEvent,
  type EventConfig,
  GameTypes,
  isPerformanceGame
} from "kadow-jetpack-model";
import React, { useMemo, useState } from "react";
import NiceModal from "@ebay/nice-modal-react";
import KC_Card from "../../../../designsystem/KC_Card/KC_Card";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../designsystem/KC_Button/KC_Button";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import { KC_Text_Sizes, KC_Weights } from "../../../../designsystem/ds_commons";
import { GameThumbnailDTO } from "../../../../model/GameDTO";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_DateTimePicker from "../../../../designsystem/KC_FormInput/KC_DateTimePicker";
import TSUtils from "../../../../utils/helpers/TSUtils";
import {
  NEUTRAL_EVENT_ID,
  DARK_MODE_ALL_EXCEPT_WHEEL_EVENT_ID,
  DARK_MODE_EVENT_ID
} from "../../../../utils/constants";
import GameCardPreview from "../../../../app/23_GameCardPreview/GameCardPreview";
import Carousel from "../../../Carousel/Carousel";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";

interface EventSelectorContentProps {
  title: string;
  description?: string;
  events: EventConfig[];
  isGamesSelectable: boolean;
  onEventSelect: (
    newEvent: ActiveEvent | null,
    isDefaultSkinSelected: boolean
  ) => void;
  handleGoBack: () => void;
  submitBtnTitle: string;
  selectedGame: GameThumbnailDTO | null;
  selectedEventId: number;
  canUsePerformanceGames: boolean;
}

export default function EventsSelectorContent(
  props: EventSelectorContentProps
) {
  const jetpackConfig = useJetpackConfigSelector();

  const [isDefaultSkinSelected, setIsDefaultSkinSelected] =
    useState<boolean>(false);

  const eventConfigsForSelectedGame = useMemo(() => {
    const res = props.events.filter((event) =>
      event.skins.some((gameType) => {
        if (props.canUsePerformanceGames) {
          return gameType.game === props.selectedGame?.name;
        }

        return (
          (!isPerformanceGame(gameType.game) ||
            gameType.game === GameTypes.KadowCrush) &&
          gameType.game === props.selectedGame?.name
        );
      })
    );

    return res.sort((a, b) => {
      const priorityA = getEventPriority(a._id);
      const priorityB = getEventPriority(b._id);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      const nameA = a.nameL?.[LANGUAGE] || a.name;
      const nameB = b.nameL?.[LANGUAGE] || b.name;
      return nameA.localeCompare(nameB);
    });
  }, [props.events, props.selectedGame, props.canUsePerformanceGames]);

  const [selectedEvent, setSelectedEvent] = useState<ActiveEvent | null>(null);

  const showDateSelector = useMemo(() => {
    return !!selectedEvent;
  }, [selectedEvent]);

  const handleSubmit = () => {
    if (!isDefaultSkinSelected && !selectedEvent) {
      void NiceModal.show(ConfirmModal, {
        title: tt("select_highlight_required_title"),
        children: tt("select_highlight_required_message"),
        hideAction: true
      });
      return;
    }
    props.onEventSelect(selectedEvent, isDefaultSkinSelected);
  };

  const renderGameCard = (
    event: EventConfig,
    isSelected: boolean,
    onSelect: (event: EventConfig) => void
  ) => {
    if (!props.selectedGame) {
      return null;
    }

    const idx = jetpackConfig?.activeEvents?.findIndex(
      (ae) => ae.id === event._id && ae.gameType === props.selectedGame?.name
    );
    const alreadyExists =
      (idx !== undefined && idx !== null && idx !== -1) || false;

    if (alreadyExists) {
      return null;
    }

    const eventGame = event.skins?.find(
      (s) => s.game === props.selectedGame?.name
    );

    if (!eventGame?.gameCardConfig) {
      return null;
    }

    const gameCardConfig = eventGame?.gameCardConfig;

    return (
      <KC_Card
        title={event.nameL?.[LANGUAGE] || event.name}
        boxShadow={false}
        useTwicPics={true}
        isGameCardPreview={true}
        className={`border carousel-slide carousel-slide-big ${
          isSelected ? "active" : ""
        }`}
        onClick={() => onSelect(event)}
      >
        <GameCardPreview gameCardConfig={gameCardConfig} eventId={event._id} />
        <div className="w-100 d-flex justify-content-between">
          <div className="w-100 d-flex justify-content-between">
            <KC_Button
              size={KC_ButtonSize.Large}
              className="d-flex border"
              isPrimaryLight={isSelected}
              isInverted={!isSelected}
              id={`${event._id}-toggle`}
              type={KC_ButtonType.Button}
              text={isSelected ? tt("game_selected") : tt("game_select")}
            />
          </div>
        </div>
      </KC_Card>
    );
  };

  const onSelectEvent = (event: EventConfig) => {
    setIsDefaultSkinSelected(false);

    let startTimestamp: number;
    let endTimestamp: number;

    if (event.permanent) {
      // Permanent events: January 1st, 2026 to December 31st, 2100
      startTimestamp = TSUtils.dateToTimestamp(new Date(2026, 0, 1, 0, 0, 0));
      endTimestamp = TSUtils.dateToTimestamp(new Date(2100, 11, 31, 23, 59, 59));
    } else {
      const dates = TSUtils.computeNextDatesForEvent(event);
      startTimestamp = dates.startM.unix();
      endTimestamp = dates.endM.unix();
    }

    const activeEvent: ActiveEvent = {
      id: event._id,
      gameType: props.selectedGame?.name || "",
      sts: startTimestamp,
      ets: endTimestamp
    };
    setSelectedEvent(activeEvent);
  };

  const IS_PERF_GAME = useMemo(() => {
    if (!props.selectedGame) {
      return false;
    }
    return isPerformanceGame(props.selectedGame.name as GameTypes);
  }, [props.selectedGame]);

  const DEFAULT_SKIN = useMemo(() => {
    if (IS_PERF_GAME) {
      return null;
    }

    if (!props.selectedGame) {
      return null;
    }

    const exists = jetpackConfig?.games?.find(
      (g) => !!props.selectedGame && g.name === props.selectedGame.name
    );

    if (exists) {
      return null;
    }

    return (
      <KC_Card
        id={`game-card-${props.selectedGame?.name}`}
        title={tt("standard_skin")}
        boxShadow={false}
        image={""}
        useTwicPics={true}
        className={`border carousel-slide ${isDefaultSkinSelected ? "active" : ""}`}
        onClick={() => {
          setSelectedEvent(null);
          setIsDefaultSkinSelected((v) => !v);
        }}
        isGameCardPreview={true}
      >
        <GameCardPreview
          gameCardConfig={props.selectedGame.config}
          eventId={props.selectedGame.eventId}
        />
        <div className="w-100 d-flex justify-content-between">
          <div className="w-100 d-flex justify-content-between">
            <KC_Button
              size={KC_ButtonSize.Large}
              className={`border d-flex ${isDefaultSkinSelected && "selected-btn"}`}
              isPrimaryLight={isDefaultSkinSelected}
              isInverted={!isDefaultSkinSelected}
              id={`${props.selectedGame.name}-toggle`}
              type={KC_ButtonType.Button}
              text={
                isDefaultSkinSelected ? tt("game_selected") : tt("game_select")
              }
            />
          </div>
        </div>
      </KC_Card>
    );
  }, [props.selectedGame, isDefaultSkinSelected]);

  const SLIDES = useMemo(() => {
    if (!jetpackConfig || !props.selectedGame) {
      return [];
    }

    const result = [
      DEFAULT_SKIN,
      ...eventConfigsForSelectedGame.map((item, idx) => {
        const isSelected =
          selectedEvent?.id === item._id &&
          !!item.skins?.some((s) => s.game === selectedEvent?.gameType);
        const card = renderGameCard(item, isSelected, (item) =>
          onSelectEvent(item)
        );

        if (card == null) {
          return null;
        }

        return <div key={idx}>{card}</div>;
      })
    ].filter((s) => !!s);

    return result;
  }, [
    DEFAULT_SKIN,
    jetpackConfig,
    selectedEvent,
    isDefaultSkinSelected,
    props.canUsePerformanceGames
  ]);

  function renderDateSelector() {
    if (!showDateSelector) {
      return (
        <div>
          <div style={{ height: 64 }}></div>
        </div>
      );
    }

    const currentEvent = props.events.find((e) => e._id === selectedEvent?.id);
    const isPermanentEvent = currentEvent?.permanent === true;

    if (isPermanentEvent) {
      return (
        <div>
          <div style={{ height: 64 }}></div>
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex flex-column flex-sm-row st-gap mx-4 my-5">
          <div className="flex-1">
            <KC_UIText weight={KC_Weights.bold} text={tt("start_date")} />
            <KC_DateTimePicker
              dateFormat={tt("date_format_datepicker")}
              onChange={(date: Date | null) => {
                if (date == null) {
                  return;
                }
                if (selectedEvent) {
                  setSelectedEvent({
                    ...selectedEvent,
                    sts: TSUtils.dateToTimestamp(date)
                  });
                }
              }}
              activeDate={
                selectedEvent?.sts
                  ? TSUtils.timestampToDate(selectedEvent?.sts)
                  : null
              }
              required={true}
            />
          </div>
          <div className="flex-1">
            <KC_UIText weight={KC_Weights.bold} text={tt("end_date")} />
            <KC_DateTimePicker
              dateFormat={tt("date_format_datepicker")}
              onChange={(date: Date | null) => {
                if (date == null) {
                  return;
                }
                if (selectedEvent) {
                  setSelectedEvent({
                    ...selectedEvent,
                    ets: TSUtils.dateToTimestamp(date)
                  });
                }
              }}
              activeDate={
                selectedEvent?.sts
                  ? TSUtils.timestampToDate(selectedEvent?.ets)
                  : null
              }
              required={true}
            />
          </div>
        </div>
      </div>
    );
  }

  if (jetpackConfig == null) {
    return <div>{tt("error")}</div>;
  }

  return (
    <div id="event-selector-content" className="mt-4 d-flex flex-column gap-sm">
      <div className="mx-4">
        <KC_Heading3 text={props.title} />
        {props.description && (
          <KC_Text
            text={props.description}
            size={KC_Text_Sizes.lg}
            weight={KC_Weights.reg}
            className="text-secondary"
          />
        )}
      </div>
      {!!SLIDES.length && (
        <div className="flex-1">
          <div className="pl-0 pl-sm-4">
            <Carousel baseId="event-selector" slides={SLIDES} />
          </div>
          {renderDateSelector()}
        </div>
      )}
      <div
        id="button-wrapper"
        className="w-100 d-flex justify-content-end gap-sm border-top p-2"
      >
        <div>
          <KC_Button
            isInverted={true}
            size={KC_ButtonSize.Large}
            text={tt("previous")}
            className="d-flex border"
            onClick={props.handleGoBack}
          />
        </div>
        {(eventConfigsForSelectedGame.length > 0 || isDefaultSkinSelected) && (
          <div>
            <KC_Button
              id="event-config-selected-game-button"
              isPrimary={true}
              size={KC_ButtonSize.Large}
              text={props.submitBtnTitle}
              disabled={props.events.length === 0 && !isDefaultSkinSelected}
              className="d-flex"
              onClick={handleSubmit}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const getEventPriority = (eventId: string): number => {
  if (eventId === "default") {
    return 0;
  }

  if (eventId === NEUTRAL_EVENT_ID) {
    return 1;
  }

  if (eventId === DARK_MODE_EVENT_ID) {
    return 2;
  }

  if (eventId === DARK_MODE_ALL_EXCEPT_WHEEL_EVENT_ID) {
    return 3;
  }

  return 4;
};
